// 统一身份服务客户端（Integral 为唯一身份来源）。
//
// 与 lib/api.ts 分开：core-api 的会话用 Authorization: Bearer，
// 而统一身份用 Integral 签发的会话（x-integral-session）。两者不混用，
// 也因此不共享同一个存储键——避免互相覆盖导致掉线。

const SESSION_KEY = 'integral_identity_session';

export function getIdentitySession(): string {
  try { return localStorage.getItem(SESSION_KEY) || ''; } catch { return ''; }
}
export function setIdentitySession(token: string): void {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch { /* storage unavailable */ }
}

export class IdentityError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'IdentityError';
    this.status = status;
  }
}

const BASE = '/api/admin/portal';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const session = getIdentitySession();
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(session ? { 'x-integral-session': session } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const ct = res.headers.get('content-type') || '';
  const parsed = ct.includes('json') ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const msg = parsed && typeof parsed === 'object' && 'error' in parsed
      ? String((parsed as { error: unknown }).error)
      : `请求失败 ${res.status}`;
    throw new IdentityError(msg, res.status);
  }
  // 后端统一 {data} 包裹
  return parsed && typeof parsed === 'object' && 'data' in parsed
    ? (parsed as { data: T }).data
    : (parsed as T);
}

export interface IdentityLoginResult {
  sessionToken: string; expiresAt: string; username: string; role: string; portalManage: boolean;
}

export const identityApi = {
  login: (username: string, password: string) =>
    request<IdentityLoginResult>('POST', '/auth/login', { username, password }),
  logout: () => request<unknown>('POST', '/auth/logout', {}),
  tokenStatus: (token: string) =>
    request<{ usable: boolean; reason?: string; expiresAt?: string }>('GET', `/auth/token-status?token=${encodeURIComponent(token)}`),
  setPassword: (token: string, password: string) =>
    request<{ ok: boolean }>('POST', '/auth/set-password', { token, password }),
  customers: <T>() => request<T[]>('GET', '/customers'),
  features: <T>() => request<T[]>('GET', '/features'),
  audit: <T>() => request<T[]>('GET', '/audit'),
  createCustomer: (body: { username: string; customer_code: string; customer_name?: string }) =>
    request<{ userId: number; setPasswordToken: string }>('POST', '/customers', body),
  setFeatures: (id: number, features: Record<string, boolean>) =>
    request<unknown>('PUT', `/customers/${id}/features`, { features }),
  disable: (id: number) => request<unknown>('POST', `/customers/${id}/disable`, {}),
  enable: (id: number) => request<unknown>('POST', `/customers/${id}/enable`, {}),
  resetPassword: (id: number) => request<{ token: string }>('POST', `/customers/${id}/reset-password`, {}),
};

// —— 员工账号管理 ——
//
// 独立前缀与**独立授权位**：客户管理走 /api/admin/portal + portal_manage，
// 员工管理走 /api/admin/staff + staff_manage。两者共用同一会话，互不越界 ——
// 因此这里不复用上面的 request/BASE，而是单独一份，只是形态保持一致。
const STAFF_BASE = '/api/admin/staff';

async function staffRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const session = getIdentitySession();
  const res = await fetch(`${STAFF_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(session ? { 'x-integral-session': session } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const ct = res.headers.get('content-type') || '';
  const parsed = ct.includes('json') ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const msg = parsed && typeof parsed === 'object' && 'error' in parsed
      ? String((parsed as { error: unknown }).error)
      : `请求失败 ${res.status}`;
    throw new IdentityError(msg, res.status);
  }
  return parsed && typeof parsed === 'object' && 'data' in parsed
    ? (parsed as { data: T }).data
    : (parsed as T);
}

export interface StaffUser {
  userId: number;
  username: string;
  email: string | null;
  displayName: string | null;
  role: string;
  isActive: boolean;
  staffManage: boolean;
  facturaManage: boolean;
  mustSetPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
}

export interface StaffAuditRow {
  id: number; actor_id: number | null; action: string;
  target_user_id: number | null; detail: string | null; created_at: string;
}

export const staffApi = {
  roles: () => staffRequest<string[]>('GET', '/roles'),
  users: () => staffRequest<StaffUser[]>('GET', '/users'),
  audit: () => staffRequest<StaffAuditRow[]>('GET', '/audit'),
  // 返回 setPasswordToken 的一次性链接明文，仅此一次；服务端不保存。
  create: (body: { username: string; email?: string; display_name?: string; role: string }) =>
    staffRequest<{ userId: number; role: string; token: string; expiresAt: string }>('POST', '/users', body),
  invite: (id: number) =>
    staffRequest<{ success: boolean; token: string; expiresAt: string }>('POST', `/users/${id}/invite`, {}),
  update: (id: number, body: { display_name?: string; email?: string; role?: string; staff_manage?: boolean; factura_manage?: boolean }) =>
    staffRequest<{ success: boolean; changed: string[] }>('PATCH', `/users/${id}`, body),
  disable: (id: number) => staffRequest<unknown>('POST', `/users/${id}/disable`, {}),
  enable: (id: number) => staffRequest<unknown>('POST', `/users/${id}/enable`, {}),
};
